import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type {
  BlockRow,
  BookmarkRow,
  LegacyResumeRow,
  PositionRow,
  SessionRow,
  SettingsRecord,
  StoredDocument,
} from './types.js';

/**
 * IndexedDB schema (PRD §10), versioned through idb's upgrade callback.
 * PD-11: IndexedDB replaces localStorage as the store — the library and
 * resume state live here, not in a 5 MB synchronous bucket.
 */
export interface VifDBSchema extends DBSchema {
  documents: {
    key: string;
    value: StoredDocument;
    indexes: { 'by-contentHash': string };
  };
  blocks: {
    /** [docId, idx] — index order within a document is the key order. */
    key: [string, number];
    value: BlockRow;
    indexes: { 'by-doc': string };
  };
  positions: {
    key: string; // docId
    value: PositionRow;
  };
  bookmarks: {
    key: [string, number]; // [docId, wordIdx]
    value: BookmarkRow;
    indexes: { 'by-doc': string };
  };
  sessions: {
    key: string;
    value: SessionRow;
  };
  settings: {
    key: string; // 'singleton'
    value: SettingsRecord;
  };
  /** Opaque KV — the store behind core's Storage interface. */
  kv: {
    key: string;
    value: string;
  };
  /** §6.6 legacy resumes awaiting re-key on re-import. */
  legacyResume: {
    key: string; // legacyHash
    value: LegacyResumeRow;
  };
}

export type VifDB = IDBPDatabase<VifDBSchema>;

export const DB_NAME = 'vif';
export const DB_VERSION = 1;

export const ALL_STORES = [
  'documents',
  'blocks',
  'positions',
  'bookmarks',
  'sessions',
  'settings',
  'kv',
  'legacyResume',
] as const;

export function openVifDb(name = DB_NAME): Promise<VifDB> {
  return openDB<VifDBSchema>(name, DB_VERSION, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        const documents = db.createObjectStore('documents', { keyPath: 'id' });
        documents.createIndex('by-contentHash', 'contentHash');
        const blocks = db.createObjectStore('blocks', { keyPath: ['docId', 'idx'] });
        blocks.createIndex('by-doc', 'docId');
        db.createObjectStore('positions', { keyPath: 'docId' });
        const bookmarks = db.createObjectStore('bookmarks', { keyPath: ['docId', 'wordIdx'] });
        bookmarks.createIndex('by-doc', 'docId');
        db.createObjectStore('sessions', { keyPath: 'id' });
        db.createObjectStore('settings');
        db.createObjectStore('kv');
        db.createObjectStore('legacyResume', { keyPath: 'legacyHash' });
      }
    },
  });
}
